import React, { lazy, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { styled } from '@mui/system';
import Typography from '@mui/material/Typography';
import i18n from 'i18next';
import { Clock, ListChecks, ArrowRight } from 'lucide-react';
import SEO from 'components/SEO';
import MainLayout from 'layouts/MainLayout';
import { getLocalizedPath } from 'helpers/getLocale';
import packs from './packs';
import useSimulation, { readBest } from './engine/useSimulation';
import { hasSimulatorContent } from './catalog';
import KpiPanel from './components/KpiPanel';
import Result from './components/Result';
import {
  Card,
  Body,
  Muted,
  Avatar,
  PrimaryButton,
  SecondaryButton,
  Pill,
  Notices
} from './components/ui';
import { motion, fadeUp, pulseRing } from './components/motion';
import { FORMAT_ICONS } from './formats';
import ChatStep from './steps/ChatStep';
import NumericStep from './steps/NumericStep';
import TriageStep from './steps/TriageStep';
import QueueStep from './steps/QueueStep';
import SpotStep from './steps/SpotStep';
import AllocateStep from './steps/AllocateStep';
import { L, t } from './strings';

const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const Breadcrumbs = lazy(() => import('components/BreadCrumbs'));

const STEP_COMPONENTS = {
  chat: ChatStep,
  choice: ChatStep,
  numeric: NumericStep,
  triage: TriageStep,
  queue: QueueStep,
  spot: SpotStep,
  allocate: AllocateStep
};

const Wrapper = styled('div')(({ theme }) => ({
  ...theme.wrapper,
  maxWidth: 1240,
  padding: '30px 20px 80px',
  boxSizing: 'border-box',
  minHeight: 'calc(100vh - 235px)',
  [theme.breakpoints.down('sm')]: { padding: '16px 16px 48px' }
}));

// Two columns that stretch to the same height: the side panel fills the
// column so both cards end on the same line.
const Layout = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 320px',
  gap: 24,
  alignItems: 'stretch',
  marginTop: 20,
  [theme.breakpoints.down('md')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
}));

const Main = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  '& > section:last-of-type': { marginBottom: 0 }
}));

const Actions = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 24
}));

const TopBar = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  margin: '20px 0 12px',
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: theme.palette.textSecondary.main,
  '& b': { color: theme.palette.textPrimary.main, fontWeight: 500 }
}));

const IconBox = styled('div')(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  backgroundColor: theme.palette.primary.salad,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 30,
  lineHeight: '38px',
  marginBottom: 10,
  [theme.breakpoints.down('sm')]: { fontSize: 24, lineHeight: '30px' }
}));

const Tagline = styled(Typography)(({ theme }) => ({
  color: theme.palette.textSecondary.main,
  marginBottom: 18
}));

const Pills = styled('div')(() => ({
  display: 'flex',
  gap: 10,
  flexWrap: 'wrap',
  alignItems: 'center',
  paddingBottom: 24,
  marginBottom: 24,
  borderBottom: '1px solid #ebedee'
}));

const List = styled('ul')(() => ({
  margin: '0 0 28px',
  paddingLeft: 0,
  listStyle: 'none',
  '& li': {
    position: 'relative',
    paddingLeft: 28,
    marginBottom: 12,
    fontFamily: 'Rubik-Regular',
    fontSize: 16,
    lineHeight: '24px'
  },
  '& li::before': {
    content: '"✓"',
    position: 'absolute',
    left: 0,
    color: '#79c912',
    fontWeight: 700
  }
}));

// Day timeline: one segment per step, coloured by how it went.
const Segments = styled('div')(() => ({
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(0, 1fr)',
  gap: 6
}));

const segColor = (score) =>
  score == null ? null : score >= 0.75 ? '#79c912' : score >= 0.4 ? '#f2c94c' : '#e5646f';

const SegBar = styled('span')(({ theme, color, current }) => ({
  display: 'block',
  height: 8,
  borderRadius: 100,
  backgroundColor: color || (current ? theme.palette.primary.salad : theme.palette.grey[100]),
  transition: 'background-color 400ms ease, transform 160ms ease',
  ...(current ? motion(`${pulseRing} 1.6s ease-out infinite`) : {})
}));

const SegLink = styled(Link)(({ theme }) => ({
  display: 'block',
  padding: '4px 0',
  textDecoration: 'none',
  '&:hover span': { transform: 'scaleY(1.4)' },
  '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, borderRadius: 4 }
}));

const SegTime = styled('span')(({ theme, current }) => ({
  display: 'block',
  marginTop: 4,
  fontFamily: 'Rubik-Regular',
  fontSize: 11,
  lineHeight: '14px',
  color: current ? theme.palette.textPrimary.main : theme.palette.textDisabled.main,
  fontWeight: current ? 500 : 400,
  [theme.breakpoints.down('md')]: { display: 'none' }
}));

function DayTimeline({ pack, state, to }) {
  return (
    <Segments role="list" aria-label={t('dayTimeline')}>
      {pack.steps.map((s, i) => {
        const answer = state.answers[s.id];
        const current = i === state.index;
        const bar = <SegBar color={segColor(answer?.score)} current={current && !answer} />;
        const label = `${s.at} · ${t('stepOf', { i: i + 1, n: pack.steps.length })}`;
        return (
          <div key={s.id} role="listitem">
            {answer && !current ? (
              <SegLink to={to(`/${i + 1}`)} title={label} aria-label={label}>
                {bar}
              </SegLink>
            ) : (
              <div style={{ padding: '4px 0' }} title={label}>
                {bar}
              </div>
            )}
            <SegTime current={current}>{s.at}</SegTime>
          </div>
        );
      })}
    </Segments>
  );
}

const Cast = styled('div')(() => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
  gap: 10,
  marginBottom: 28
}));

const Person = styled('div')(({ theme, delay }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  borderRadius: 12,
  backgroundColor: theme.palette.grey[50],
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  lineHeight: '18px',
  minWidth: 0,
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`),
  '& small': {
    display: 'block',
    fontSize: 12,
    color: theme.palette.textTertiary.main,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}));

export default function SimulatorPlay() {
  const { simId, page } = useParams();
  const pack = packs[simId];
  if (!pack) return <NotFound />;
  return <Game key={simId} pack={pack} page={page} />;
}

function NotFound() {
  return (
    <MainLayout grey>
      <SEO title="Simulator" noIndex />
      <Header />
      <Wrapper>
        <Body>{t('notFound')}</Body>
        <SecondaryButton
          component={Link}
          to={getLocalizedPath('/simulator')}
          sx={{ marginTop: '16px' }}>
          {t('tryAnother')}
        </SecondaryButton>
      </Wrapper>
      <Footer />
    </MainLayout>
  );
}

// URL → view. `page` is undefined (intro), '1'..'N' (a step) or 'result'.
const parsePage = (page, total) => {
  if (page == null) return { view: 'intro' };
  if (page === 'result') return { view: 'result' };
  const n = Number(page);
  if (Number.isInteger(n) && n >= 1 && n <= total) return { view: 'step', index: n - 1 };
  return { view: 'invalid' };
};

function Game({ pack, page }) {
  const navigate = useNavigate();
  const sim = useSimulation(pack);
  const {
    state,
    step,
    total,
    answeredCount,
    finished,
    answered,
    notices,
    reset,
    goto,
    submit,
    summary
  } = sim;
  const base = `/simulator/${pack.id}`;
  const to = (suffix = '') => getLocalizedPath(base + suffix);
  const target = parsePage(page, total);

  // Steps may be visited up to the first unanswered one; anything further
  // (or the result before the end) redirects to the right place.
  useEffect(() => {
    if (target.view === 'step') {
      if (target.index > answeredCount) navigate(to(`/${answeredCount + 1}`), { replace: true });
      else if (target.index !== state.index || state.visited < target.index) goto(target.index);
    } else if (target.view === 'result' && !finished) {
      navigate(to(answeredCount ? `/${answeredCount + 1}` : ''), { replace: true });
    } else if (target.view === 'invalid') {
      navigate(to(), { replace: true });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const best = useMemo(() => readBest(pack.id), [pack.id, finished]);
  const kpiLabels = useMemo(() => {
    const m = { energy: t('energy'), trust: t('trust') };
    pack.kpis.forEach((k) => {
      m[k.key] = L(k.label);
    });
    return m;
  }, [pack]);
  const FormatIcon = FORMAT_ICONS[pack.format];
  const formatLabel = t('formats')[pack.format];

  const start = () => {
    reset();
    navigate(to('/1'));
  };
  const next = () => navigate(to(state.index + 1 >= total ? '/result' : `/${state.index + 2}`));
  const restart = () => {
    reset();
    navigate(to());
  };

  const StepComponent = step ? STEP_COMPONENTS[step.type] : null;
  const view = target.view === 'step' && target.index === state.index ? 'step' : target.view;
  const breadcrumbs = (
    <Breadcrumbs
      breadcrumbs={[
        { label: 'Home', link: '/' },
        { label: i18n.t('Menu.Professions'), link: '/professions', raw: true },
        { label: t('hubBreadcrumb'), link: '/simulator', raw: true },
        { label: L(pack.occupationName), raw: true }
      ]}
    />
  );
  const pageTitle =
    view === 'result'
      ? `${t('resultTitle')} · ${L(pack.title)}`
      : view === 'step'
        ? `${t('stepOf', { i: state.index + 1, n: total })} · ${L(pack.title)}`
        : L(pack.title);

  return (
    <MainLayout grey>
      {/* Only the intro is indexable; steps and the result are per-player states. */}
      <SEO
        title={`${pageTitle} | ${t('hubBreadcrumb')}`}
        description={view === 'intro' ? L(pack.intro) : L(pack.tagline)}
        noIndex={view !== 'intro' || !hasSimulatorContent()}
      />
      <Header />
      <Wrapper>
        {breadcrumbs}

        {view === 'intro' && (
          <Layout>
            <Main>
              <Card>
                <IconBox>{FormatIcon && <FormatIcon size={28} strokeWidth={1.8} />}</IconBox>
                <Title variant="h1">{L(pack.title)}</Title>
                <Tagline variant="body1">{L(pack.tagline)}</Tagline>
                <Pills>
                  <Pill>
                    {FormatIcon && <FormatIcon size={14} />} {formatLabel}
                  </Pill>
                  <Pill>
                    <Clock size={14} /> {t('minutes', { n: pack.minutes })}
                  </Pill>
                  <Pill>
                    <ListChecks size={14} /> {t('steps', { n: total })}
                  </Pill>
                  {best && <Muted>{t('bestSoFar', { s: best.score })}</Muted>}
                </Pills>
                <Body style={{ marginBottom: 24 }}>{L(pack.intro)}</Body>
                {pack.people?.length > 0 && (
                  <>
                    <Muted style={{ marginBottom: 12 }}>{t('yourTeam')}</Muted>
                    <Cast>
                      {pack.people.map((p, i) => (
                        <Person key={p.key} delay={150 + i * 60}>
                          <Avatar person={p} size={36} />
                          <div style={{ minWidth: 0 }}>
                            {L(p.name)}
                            <small>{L(p.role)}</small>
                          </div>
                        </Person>
                      ))}
                    </Cast>
                  </>
                )}
                <Muted style={{ marginBottom: 12 }}>{t('whatYouWillDo')}</Muted>
                <List>
                  {t('introBullets').map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </List>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <PrimaryButton onClick={start} endIcon={<ArrowRight size={16} />}>
                    {t('start')}
                  </PrimaryButton>
                  {answeredCount > 0 && !finished && (
                    <SecondaryButton component={Link} to={to(`/${answeredCount + 1}`)}>
                      {t('continue')} · {t('stepOf', { i: answeredCount + 1, n: total })}
                    </SecondaryButton>
                  )}
                  {finished && (
                    <SecondaryButton component={Link} to={to('/result')}>
                      {t('finish')}
                    </SecondaryButton>
                  )}
                </div>
              </Card>
            </Main>
            <KpiPanel pack={pack} kpis={state.kpis} lastFx={null} />
          </Layout>
        )}

        {view === 'step' && step && (
          <>
            <TopBar>
              <span>
                <b>{L(pack.title)}</b> · {formatLabel}
              </span>
              <span>
                <Clock size={14} style={{ verticalAlign: -2 }} /> {step.at} ·{' '}
                {t('stepOf', { i: state.index + 1, n: total })}
              </span>
            </TopBar>
            <DayTimeline pack={pack} state={state} to={to} />
            <Layout>
              <Main>
                <Notices notices={notices} />
                <StepComponent
                  key={step.id}
                  step={step}
                  pack={pack}
                  answered={answered}
                  onSubmit={submit}
                  kpiLabels={kpiLabels}
                />
                {answered && (
                  <Actions>
                    <PrimaryButton onClick={next} endIcon={<ArrowRight size={16} />}>
                      {state.index + 1 >= total ? t('finish') : t('continue')}
                    </PrimaryButton>
                  </Actions>
                )}
              </Main>
              <KpiPanel pack={pack} kpis={state.kpis} lastFx={state.lastFx} />
            </Layout>
          </>
        )}

        {view === 'result' && summary && (
          <div style={{ marginTop: 20 }}>
            <Result pack={pack} summary={summary} kpis={state.kpis} onRestart={restart} />
          </div>
        )}
      </Wrapper>
      <Footer />
    </MainLayout>
  );
}
